import { toast } from 'sonner';

/**
 * Centralised social sharing helper.
 *
 * @param {string} channel  One of: native, whatsapp, facebook, linkedin,
 *                          twitter, instagram, tiktok, copy.
 * @param {object} opts
 * @param {string} opts.url       Clean public URL (what users see in the address bar).
 * @param {string} opts.ogUrl     Server-side OG endpoint (used by scrapers to
 *                                render title/image/description previews).
 * @param {string} opts.title     Human-readable title (e.g. article/job title).
 * @param {string} [opts.subject] Optional subject line for messaging channels.
 */
export async function shareToSocial(channel, { url, ogUrl, title, subject }) {
  const shareTitle = subject || title;
  const encOg = encodeURIComponent(ogUrl || url);
  const encUrl = encodeURIComponent(url);
  const encTitle = encodeURIComponent(shareTitle);

  const copyLink = async (msg) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(msg || 'Lien copié dans le presse-papier');
    } catch (e) {
      toast.error('Impossible de copier le lien');
    }
  };

  // ── Native (mobile Web Share API) ───────────────────────────────────────
  if (channel === 'native') {
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: `Je vous partage : ${title}`,
          url: ogUrl || url,
        });
        return;
      } catch (e) {
        if (e && e.name === 'AbortError') return;
        // fall through to copy
      }
    }
    await copyLink('Partage natif indisponible — lien copié.');
    return;
  }

  // ── Copy ────────────────────────────────────────────────────────────────
  if (channel === 'copy') {
    await copyLink();
    return;
  }

  // ── WhatsApp (prefilled message) ────────────────────────────────────────
  if (channel === 'whatsapp') {
    const msg = `${shareTitle}\n${ogUrl || url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank', 'noopener,noreferrer');
    return;
  }

  // ── Instagram / TikTok : no public web share URL exists ────────────────
  // On mobile → try native share so the OS proposes the installed app.
  // On desktop → copy the link with a channel-specific hint.
  if (channel === 'instagram' || channel === 'tiktok') {
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: `Je vous partage : ${title}`,
          url: ogUrl || url,
        });
        return;
      } catch (e) {
        if (e && e.name === 'AbortError') return;
      }
    }
    const label = channel === 'instagram' ? 'Instagram' : 'TikTok';
    await copyLink(`Lien copié — collez-le dans ${label}`);
    return;
  }

  // ── Facebook : Sharer requires the user to be logged in. If FB redirects
  //    to the login page we can't detect it (cross-origin), so we ALSO copy
  //    the link and show a helpful toast so the user can paste anywhere.
  if (channel === 'facebook') {
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encOg}`,
      '_blank',
      'noopener,noreferrer',
    );
    try {
      await navigator.clipboard.writeText(url);
      toast.info(
        'Astuce : si Facebook ouvre l\'accueil, connectez-vous d\'abord. Le lien est déjà copié pour vous.',
        { duration: 6000 },
      );
    } catch (e) { /* ignore clipboard failure */ }
    return;
  }

  // ── LinkedIn / Twitter : straight sharer URLs ───────────────────────────
  const targets = {
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encOg}`,
    twitter: `https://twitter.com/intent/tweet?url=${encOg}&text=${encTitle}`,
  };
  if (targets[channel]) {
    window.open(targets[channel], '_blank', 'noopener,noreferrer');
  }
}
